import React from 'react';
import PropTypes from 'prop-types';
import * as immutable from 'immutable';
import {List} from 'immutable';
import ProfileKeywordEditor from 'components/admin/topics/ProfileKeywordEditor';
import NewFilterWordProfile from 'records/admin/topics/NewFilterWordProfile';
import {topicLink} from 'components/admin/topics/common';
import Button from '@material-ui/core/Button';
import SelectField from 'components/pure/utils/form/SelectField';
import TrashIcon from 'components/pure/utils/icon/TrashIcon';
import './InlineProfileEditor.styl';
import GenericLink from 'components/GenericLink';
import EditOrCloneDialog from 'components/admin/topics/EditOrCloneDialog';

/**
 * Get options for the add topic dropdown.
 *
 * Topics that can be added to a profile are those in the account that are not already
 * attached to the profile.
 *
 * @param topicsInAccount {Immutable.List<NewFilterWord>} all topics in the account.
 * @param profileTopics {Immutable.List<NewFilterWord>} topics attached to the profile.
 * @return {Immutable.Iterable<any, {label: string, value: NewFilterWord}>} label and value pairs
 * for topics in the account but not already attached to the profile.
 */
function topicsOptions(topicsInAccount, profileTopics) {
  const profileTopicsSet = profileTopics.toSet();
  const topics = topicsInAccount.filterNot(topic => profileTopicsSet.has(topic));
  return topics.map(topic => {
    return {
      label: topic.word,
      value: topic,
    };
  });
}

/**
 * Add a topic to a profile.
 *
 * @param onChangeProfile {function(NewFilterWordProfile)} on change handler for profiles. Will be
 * called with the updated profile as an argument.
 * @param profile {NewFilterWordProfile} the profile to update.
 * @param topic {NewFilterWord} the topic to add to the profile.
 */
function addTopicToProfile(onChangeProfile, profile, topic) {
  onChangeProfile(profile.update('filterWords', filterWords => filterWords.push(topic)));
}

/**
 * Remove a topic from a profile.
 *
 * @param onChangeProfile {function(NewFilterWordProfile)} on change handler for profiles. Will be
 * called with the updated profile as an argument.
 * @param profile {NewFilterWordProfile} the profile to update.
 * @param topic {NewFilterWord} the topic to remove from the profile.
 */
function removeTopicFromProfile(onChangeProfile, profile, topic) {
  const topicIndex = profile.filterWords.findIndex(fw => fw === topic);
  onChangeProfile(profile.deleteIn(['filterWords', topicIndex]));
}

/**
 * Profile editor that shows up when you expand the profile selected for a team or department.
 */
export default class InlineProfileEditor extends React.PureComponent {
  static propTypes = {
    editable: PropTypes.bool.isRequired,
    editing: PropTypes.bool.isRequired,
    nameIsValid: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onChangeProfile: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onSaveKeywords: PropTypes.func.isRequired,
    onStartEditor: PropTypes.func.isRequired,
    profileDepartmentAndTeamUsage: PropTypes.instanceOf(immutable.Map).isRequired,
    profile: PropTypes.instanceOf(NewFilterWordProfile),
    topicsInAccount: PropTypes.instanceOf(immutable.List).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      editOrCloneDialogOpen: false,
      keywordsOpen: false,
    };
  }

  openEditOrCloneDialog() {
    this.setState({
      editOrCloneDialogOpen: true,
    });
  }

  closeEditOrCloneDialog() {
    this.setState({
      editOrCloneDialogOpen: false,
    });
  }

  openKeywordPopup() {
    this.setState({
      keywordsOpen: true,
    });
  }

  closeKeywordPopup() {
    this.setState({
      keywordsOpen: false,
    });
  }

  render() {
    const {
      editable,
      editing,
      nameIsValid,
      onCancel,
      onChangeProfile,
      onSave,
      onSaveKeywords,
      onStartEditor,
      profile,
      profileDepartmentAndTeamUsage,
      topicsInAccount,
    } = this.props;
    const {editOrCloneDialogOpen, keywordsOpen} = this.state;
    if (!profile) {
      // Can happen when the user clicks 'X' to remove the profile from the department or team.
      return null;
    }
    const profileKeywords = profile ? profile.keywords() : List();
    const profileTopics = profile ? profile.topics() : List();
    return (
      <div className="inline-profile-editor">
        <ProfileKeywordEditor
          editing={editing}
          keywords={profileKeywords}
          profile={profile}
          open={keywordsOpen}
          onSave={onSaveKeywords}
          onClose={() => this.closeKeywordPopup()}
        />
        <div>
          <GenericLink
            onClick={() => {
              // Disallow opening if name is invalid. The keyword popup lets you save, and doesn't
              // check name validity itself. Will get 500 back from the server trying to save.
              (!editing || nameIsValid) && this.openKeywordPopup();
            }}>
            {`${profileKeywords.size} keywords`}
          </GenericLink>
        </div>
        {profileTopics.map(topic => (
          <div className="inline-topic" key={topic.filterWordId}>
            {topicLink(topic, `Topic: ${topic.word}`)}
            {editing && (
              <TrashIcon onClick={() => removeTopicFromProfile(onChangeProfile, profile, topic)} />
            )}
          </div>
        ))}
        {editable && (
          <div>
            {editing ? (
              <div>
                <div>Add topic:</div>
                <SelectField
                  options={topicsOptions(topicsInAccount, profileTopics)}
                  onChange={topic => addTopicToProfile(onChangeProfile, profile, topic)}
                />
                <div className="editor-buttons">
                  <Button
                    variant="contained"
                    color="primary"
                    className="ev-button"
                    disabled={!nameIsValid}
                    onClick={() => {
                      onSave();
                    }}>
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    className="ev-button"
                    onClick={() => {
                      onCancel();
                    }}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="editor-buttons">
                <EditOrCloneDialog
                  open={editOrCloneDialogOpen}
                  onClose={() => this.closeEditOrCloneDialog()}
                  onClone={() => {
                    onStartEditor(profile.cloneNew(), true);
                    this.closeEditOrCloneDialog();
                  }}
                  onEdit={() => {
                    onStartEditor(profile, false);
                    this.closeEditOrCloneDialog();
                  }}
                />
                <Button
                  variant="outlined"
                  className="ev-button"
                  onClick={() => {
                    const departmentsUsingProfile = profileDepartmentAndTeamUsage
                      .get(profile.filterWordProfileId)
                      .map(usage => usage.department.id)
                      .toSet();
                    if (departmentsUsingProfile.size > 1) {
                      this.openEditOrCloneDialog();
                    } else {
                      onStartEditor(profile, false);
                    }
                  }}>
                  Edit
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}
